/**
 * The words of "Grow thinks this is bee balm (Monarda), a perennial flower in
 * the mint family. File it under Perennial flower?", built from what is known
 * of a suggestion (UnknownPlantCard). A part not known is left out, never
 * guessed; Spanish names the family by its Latin name.
 *
 * Words (en/es): unknown.* (thinks, thinksKind, thinksFamily,
 * thinksKindFamily, nameLatin, kinds.*, broad.*, broadParent.*).
 *
 * @module suggestionWords
 */

import type { PlantSuggestion } from '@/shared/api/plantNames';

/** The words a suggestion needs, so the sentence can be built (and tested) apart from the card. */
type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * Words lowered and run together, for telling whether two names say the same.
 *
 * @param text - A name.
 */
const plain = (text: string | null | undefined): string => String(text ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');

/**
 * The plant it goes under, as the question names it: a broad flower parent in
 * the app's own words ("Perennial flower"), any other by its name.
 *
 * @param t - The translator.
 * @param suggestion - What Grow thinks it is.
 */
export function parentLabel(t: Translate, suggestion: PlantSuggestion): string {
  return suggestion.broad ? t(`unknown.broadParent.${suggestion.broad}`) : suggestion.plantName;
}

/**
 * "Grow thinks this is bee balm (Monarda), a perennial flower in the mint family."
 *
 * @param t - The translator.
 * @param suggestion - What Grow thinks it is.
 * @param language - The app's language: Spanish names the family in Latin.
 */
export function suggestionSentence(t: Translate, suggestion: PlantSuggestion, language: string): string {
  const latin = suggestion.latin && !plain(suggestion.latin).includes(plain(suggestion.name)) ? suggestion.latin : null;
  const name = latin ? t('unknown.nameLatin', { name: suggestion.name, latin }) : suggestion.name;
  // A known plant is that plant: its kind is said by the question, not here.
  const kind = suggestion.known ? null
    : suggestion.broad ? t(`unknown.broad.${suggestion.broad}`)
      : suggestion.kind ? t(`unknown.kinds.${suggestion.kind}`) : null;
  const family = language.startsWith('en') ? suggestion.family : suggestion.familyLatin;
  if (kind && family) return t('unknown.thinksKindFamily', { name, kind, family });
  if (kind) return t('unknown.thinksKind', { name, kind });
  if (family) return t('unknown.thinksFamily', { name, family });
  return t('unknown.thinks', { name });
}
